import { HugeiconsIcon } from "@hugeicons/react";
import { Target01Icon, TrendingUpIcon } from "@hugeicons-pro/core-bulk-rounded";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { MetricTiles } from "@/components/dashboard/metric-tiles";
import { ChartCard } from "@/components/dashboard/chart-card";
import { TrendArea } from "@/components/dashboard/charts";
import { BreakdownTable } from "@/components/dashboard/breakdown-table";
import { brandLabelCol, spendCol, revenueCol, roasCol, merCol, exactReachCol, ordersCol } from "@/components/dashboard/columns";
import { BenchmarkCell, BenchmarkSection } from "@/components/dashboard/benchmark-cell";

import { Skeleton } from "@/components/ui/skeleton";
import { FadeIn } from "@/components/ui/motion";
import { seriesColor } from "@/lib/chart-theme";
import { useTotals, useSeries, useBreakdown } from "@/lib/use-metrics";
import { useReference } from "@/lib/use-reference";
import { useMetricMoney, useMoney } from "@/lib/use-money";
import { benchmarkTarget } from "@/lib/benchmarks";
import { useBenchmarkEditor } from "@/lib/use-benchmark-editor";
import { useFilters } from "@/lib/filters";
import { useExactReach, reachCaveats } from "@/lib/use-reach";

export function Overview() {
  const filters = useFilters();
  const { metrics, benchmarks, brands, fx } = useReference();
  const m = useMetricMoney();
  const benchmarkMoney = useMoney();
  const { saveByKey } = useBenchmarkEditor();
  // Money is blended across every activity, exactly as on the E-commerce page
  // and as Converge reports it: awareness spend in a market drives that market's
  // sales, so charging it only against awareness understated spend (7 days for
  // Prado USA: $25.7k ecom-only against $35.4k actually spent) and inflated ROAS.
  // Reach and engagement stay awareness-scoped so paid ecom reach can't
  // contaminate them. Region/brand/date still apply throughout.
  const blended = useTotals("all");
  const awareness = useTotals("awareness");
  const jobs = useTotals("job_ads");
  const blendedSeries = useSeries("all");
  const awareSeries = useSeries("awareness");
  const byBrand = useBreakdown("brand", "all");
  // Reach is unique people per platform and period, asked of the platforms
  // themselves (a sum of daily rows counted people once per day).
  const reach = useExactReach({ activity: "awareness" });
  const reachPrev = useExactReach({ activity: "awareness", previous: true });
  const r = reach.get();
  const rPrev = reachPrev.get();
  const reachComparable = !!r?.exact && !!rPrev?.exact;
  const reachCaveat = reachCaveats(r).join(" ") || undefined;

  const loading = blended.isPending || awareness.isPending || jobs.isPending;

  const e = blended.data?.kpis;
  const a = awareness.data?.kpis;
  const j = jobs.data?.kpis;
  const values = {
    revenue: e?.revenue, spend: e?.spend, roas: e?.roas, mer: e?.mer, orders: e?.orders,
    // website_visitors comes from GA4, which lives in the awareness scope.
    website_visitors: a?.website_visitors,
    reached_accounts: r?.reach ?? null, organic_engagement_rate: r?.er ?? null,
    applications: j?.applications,
  };
  const prev = {
    revenue: blended.data?.prev.revenue, spend: blended.data?.prev.spend, roas: blended.data?.prev.roas,
    mer: blended.data?.prev.mer, orders: blended.data?.prev.orders,
    website_visitors: awareness.data?.prev.website_visitors,
    reached_accounts: reachComparable ? rPrev!.reach : null, organic_engagement_rate: rPrev?.er ?? null,
    applications: jobs.data?.prev.applications,
  };

  const brandColumns = [
    brandLabelCol("Merk", brands), spendCol(m.fmt), revenueCol(m.fmt), roasCol(), merCol(), exactReachCol(reach, "brand", "Bereik (awareness)"), ordersCol(),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overzicht"
        icon={TrendingUpIcon}
        title="Marketing overzicht"
        description={filters.channel === 'all'
          ? "Omzet: webshopcijfers waar beschikbaar (GA4 in Europa, Converge voor prado.com); overige merken gebruiken kanaalattributie. Dit is niet de som van de advertentiekanalen."
          : "Omzet: conversiewaarde toegeschreven aan het geselecteerde kanaal. Dit is een andere meetbasis dan de totale webshopomzet."}
        tabs={<GlobalFilterBar showActivity={false} />}
      />

      {loading ? (
        <Skeleton className="h-44 rounded-2xl" />
      ) : (
        <MetricTiles
          keys={["revenue", "spend", "roas", "mer", "orders", "website_visitors", "reached_accounts", "organic_engagement_rate", "applications"]}
          values={values}
          prev={prev}
          metrics={metrics}
          columns={3}
          money={m.fmt}
          notes={{ reached_accounts: reachCaveat }}
          pending={reach.loading ? ["reached_accounts", "organic_engagement_rate"] : []}
        />
      )}

      <FadeIn delay={0.05}>
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Omzet & uitgaven" subtitle="Alle activiteiten · dagelijks" bodyClassName="h-80">
            <TrendArea
              data={blendedSeries.data ?? []}
              moneyFmt={m.fmt}
              series={[
                { dataKey: "revenue", name: "Omzet", color: seriesColor("revenue"), kind: "currency", axis: "left" },
                { dataKey: "spend", name: "Uitgaven", color: seriesColor("spend"), kind: "currency", axis: "right" },
              ]}
            />
          </ChartCard>
          <ChartCard title="Bereik & engagement" subtitle="Awareness · per dag, dagbereik van elk platform opgeteld" bodyClassName="h-80">
            <TrendArea
              data={awareSeries.data ?? []}
              series={[
                { dataKey: "reach", name: "Dagbereik", color: seriesColor("reach"), axis: "left" },
                { dataKey: "engagements", name: "Engagement", color: seriesColor("engagements"), axis: "right" },
              ]}
            />
          </ChartCard>
        </div>
      </FadeIn>

      <FadeIn delay={0.1}>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="min-w-0 lg:col-span-2">
            <BreakdownTable title="Per merk" description="Uitgaven, omzet en rendement per merk over de gekozen periode." rows={byBrand.data ?? []} columns={brandColumns} rowKey={(r) => r.key} exportName="Prado Pulse — Overzicht per merk" />
          </div>
          <BenchmarkSection title="Doelen" icon={Target01Icon} columns={1}>
            <BenchmarkCell label="ROAS (blended)" actual={e?.roas ?? null} target={benchmarkTarget(benchmarks, "roas", null)} unit="ratio" higherIsBetter money={m.fmt}
              onSaveTarget={(v) => saveByKey({ metric_key: "roas", activity_type: "ecom", region: null, brand_id: null }, v)} />
            <BenchmarkCell label="Engagement rate (awareness)" actual={r?.er ?? null} target={benchmarkTarget(benchmarks, "organic_engagement_rate", null)} unit="percent" higherIsBetter money={m.fmt}
              onSaveTarget={(v) => saveByKey({ metric_key: "organic_engagement_rate", activity_type: "awareness", region: null, brand_id: null }, v)} />
            <BenchmarkCell label="Kost/sollicitatie (jobs)" actual={j?.cost_per_application != null && fx[m.code] ? j.cost_per_application * fx[m.code]! : null} target={benchmarkTarget(benchmarks, "cost_per_application", null)} unit="currency" higherIsBetter={false} money={benchmarkMoney.fmt}
              onSaveTarget={(v) => saveByKey({ metric_key: "cost_per_application", activity_type: "job_ads", region: null, brand_id: null }, v)} />
          </BenchmarkSection>
        </div>
      </FadeIn>
    </div>
  );
}
